import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import Modal from '@/components/shared/Modal';
import { useSuppliers, todayISO, getPriceStatus } from '@/hooks/useSuppliers';
import { formatDate, formatPriceCt } from '@/utils/formatters';
import {
  FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiPackage,
  FiMail, FiPhone, FiUser, FiDollarSign, FiSave, FiX, FiAlertCircle
} from 'react-icons/fi';

/**
 * Formular fuer einen Einkaufspreis.
 * Wird sowohl hier (Versorger schon bekannt) als auch in der Preisuebersicht
 * (Versorger wird ausgewaehlt) verwendet — deshalb exportiert.
 */
export const PriceForm = ({ suppliers = [], initialData, onSave, onCancel, supplierId, saving = false, error = '' }) => {
  const [form, setForm] = useState(initialData || {
    supplier_id: supplierId ?? '',
    company_id: null,
    label: '',
    price_ct_kwh: '',
    price_type: 'fixed',
    contract_duration_months: 12,
    min_consumption_kwh: '',
    max_consumption_kwh: '',
    valid_from: todayISO(),
    valid_until: '',
    conditions: '',
    source: 'Manuell',
  });
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.supplier_id) { setLocalError('Bitte einen Versorger auswaehlen.'); return; }
    if (!form.label.trim() || form.price_ct_kwh === '') { setLocalError('Tarifname und Einkaufspreis sind Pflichtfelder.'); return; }
    if (form.valid_until && form.valid_from && form.valid_until < form.valid_from) {
      setLocalError('"Gueltig bis" liegt vor "Gueltig ab".');
      return;
    }
    setLocalError('');
    onSave({
      ...form,
      supplier_id: Number(form.supplier_id),
      // Leere Datums-/Zahlenfelder als NULL senden — '' waere fuer Postgres ungueltig.
      price_ct_kwh: Number(form.price_ct_kwh),
      min_consumption_kwh: form.min_consumption_kwh === '' ? null : Number(form.min_consumption_kwh),
      max_consumption_kwh: form.max_consumption_kwh === '' ? null : Number(form.max_consumption_kwh),
      contract_duration_months: form.contract_duration_months === '' ? null : Number(form.contract_duration_months),
      valid_from: form.valid_from || todayISO(),
      valid_until: form.valid_until || null,
      conditions: form.conditions?.trim() ? form.conditions.trim() : null,
    });
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
  const shownError = error || localError;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {shownError && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{shownError}</p>
      )}

      {supplierId == null && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Versorger *</label>
          <select
            value={form.supplier_id}
            onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}
            className={inputClass}
            required
          >
            <option value="">– bitte waehlen –</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {suppliers.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">Es ist noch kein Versorger angelegt.</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tarifname *</label>
          <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} className={inputClass} placeholder="z.B. Gewerbe Basis" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Einkaufspreis (ct/kWh) *</label>
          <input type="number" step="0.01" value={form.price_ct_kwh} onChange={e => setForm(f => ({ ...f, price_ct_kwh: e.target.value }))} className={inputClass} placeholder="9.20" required />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preistyp</label>
          <select value={form.price_type} onChange={e => setForm(f => ({ ...f, price_type: e.target.value }))} className={inputClass}>
            <option value="fixed">Festpreis</option>
            <option value="indexed">Indexiert</option>
            <option value="dynamic">Dynamisch</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Laufzeit (Monate)</label>
          <input type="number" value={form.contract_duration_months} onChange={e => setForm(f => ({ ...f, contract_duration_months: e.target.value }))} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Quelle</label>
          <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className={inputClass}>
            <option value="Manuell">Manuell</option>
            <option value="E-Mail">E-Mail</option>
            <option value="PDF">PDF</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min. Verbrauch (kWh)</label>
          <input type="number" value={form.min_consumption_kwh} onChange={e => setForm(f => ({ ...f, min_consumption_kwh: e.target.value }))} className={inputClass} placeholder="50000" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max. Verbrauch (kWh)</label>
          <input type="number" value={form.max_consumption_kwh} onChange={e => setForm(f => ({ ...f, max_consumption_kwh: e.target.value }))} className={inputClass} placeholder="500000" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gueltig ab *</label>
          <input type="date" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gueltig bis</label>
          <input type="date" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))} className={inputClass} />
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Ausserhalb dieses Zeitraums fliesst der Preis nicht mehr in Kalkulationen ein.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bedingungen</label>
        <textarea value={form.conditions || ''} onChange={e => setForm(f => ({ ...f, conditions: e.target.value }))} className={inputClass + ' h-16 resize-none'} placeholder="Vertragsbedingungen..." />
      </div>
      <div className="flex justify-end space-x-3 pt-2 border-t">
        {onCancel && <Button type="button" variant="secondary" icon={FiX} onClick={onCancel}>Abbrechen</Button>}
        <Button type="submit" icon={FiSave} loading={saving} disabled={saving}>Speichern</Button>
      </div>
    </form>
  );
};

const STATUS_STYLES = {
  aktiv: 'bg-green-100 text-green-700',
  geplant: 'bg-blue-100 text-blue-700',
  abgelaufen: 'bg-red-100 text-red-700',
  inaktiv: 'bg-gray-100 text-gray-600',
};

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getSupplierById, getPricesForSupplier, addPrice, deletePrice, updateSupplier,
    marginSettings, loading, loadError, reload,
  } = useSuppliers();
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [actionError, setActionError] = useState('');

  const supplier = getSupplierById(id);
  const supplierPrices = getPricesForSupplier(id);

  if (loading) {
    return <div className="text-center py-20 text-gray-500">Lade Versorger …</div>;
  }

  // Nur ein LADEfehler darf die Detailseite ersetzen. Ein fehlgeschlagenes
  // Speichern laesst die vorhandenen Daten unberuehrt und wird unten im
  // Formular bzw. als Hinweis ueber der Preisliste angezeigt.
  if (loadError) {
    return (
      <div className="max-w-lg mx-auto py-16">
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5 shrink-0" size={18} />
            <div>
              <p className="font-semibold text-red-800">Versorgerdaten konnten nicht geladen werden</p>
              <p className="text-sm text-red-700 mt-1">{loadError}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={reload}>Erneut versuchen</Button>
                <Button size="sm" variant="secondary" onClick={() => navigate('/app/versorger')}>Zurueck</Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Versorger nicht gefunden</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/app/versorger')}>Zurueck</Button>
      </div>
    );
  }

  const handleAddPrice = async (data) => {
    setSaving(true);
    setFormError('');
    try {
      await addPrice(data);
      setShowPriceModal(false);
    } catch (err) {
      setFormError(err.message || 'Tarif konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrice = async (price) => {
    if (!window.confirm(`Tarif "${price.label}" loeschen? Er fliesst danach in keine Kalkulation mehr ein.`)) return;
    setActionError('');
    try {
      await deletePrice(price.id);
    } catch (err) {
      setActionError(err.message || 'Tarif konnte nicht geloescht werden');
    }
  };

  const handleEditSupplier = () => {
    setEditForm({
      name: supplier.name || '',
      contact_name: supplier.contact_name || '',
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      notes: supplier.notes || '',
    });
    setFormError('');
    setShowEditModal(true);
  };

  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      await updateSupplier(id, editForm);
      setShowEditModal(false);
    } catch (err) {
      setFormError(err.message || 'Versorger konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/app/versorger')} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <FiArrowLeft size={20} />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <FiPackage className="text-primary-600" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{supplier.name}</h1>
              <p className="text-gray-500 text-sm">{supplierPrices.length} hinterlegte Tarife</p>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="secondary" icon={FiEdit2} onClick={handleEditSupplier}>Bearbeiten</Button>
          <Button icon={FiPlus} onClick={() => { setFormError(''); setShowPriceModal(true); }}>Neuer Tarif</Button>
        </div>
      </div>

      {/* Schreibfehler: die angezeigten Daten sind gueltig, nur die Aktion ist
          fehlgeschlagen. Deshalb ein Hinweis statt einer Fehlerseite — und
          keine gesperrten Buttons. */}
      {actionError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-red-800">Aenderung wurde nicht gespeichert</p>
              <p className="text-sm text-red-700 mt-0.5">{actionError}</p>
            </div>
            <button
              onClick={() => setActionError('')}
              className="p-1 text-red-400 hover:text-red-700 rounded"
              title="Hinweis ausblenden"
            >
              <FiX size={16} />
            </button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Kontaktdaten */}
        <Card>
          <Card.Title>Kontakt</Card.Title>
          <div className="mt-3 space-y-3 text-sm">
            {supplier.contact_name && (
              <div className="flex items-center space-x-2 text-gray-700">
                <FiUser size={14} className="text-gray-400" />
                <span>{supplier.contact_name}</span>
              </div>
            )}
            {supplier.contact_email && (
              <a href={`mailto:${supplier.contact_email}`} className="flex items-center space-x-2 text-gray-600 hover:text-primary-600">
                <FiMail size={14} className="text-gray-400" />
                <span>{supplier.contact_email}</span>
              </a>
            )}
            {supplier.contact_phone && (
              <a href={`tel:${supplier.contact_phone}`} className="flex items-center space-x-2 text-gray-600 hover:text-primary-600">
                <FiPhone size={14} className="text-gray-400" />
                <span>{supplier.contact_phone}</span>
              </a>
            )}
            {!supplier.contact_name && !supplier.contact_email && !supplier.contact_phone && (
              <p className="text-gray-400">Keine Kontaktdaten hinterlegt</p>
            )}
            {supplier.notes && <p className="text-gray-500 pt-2 border-t border-gray-100">{supplier.notes}</p>}
          </div>
        </Card>

        {/* Preisliste */}
        <div className="lg:col-span-2">
          <Card padding={false}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Tarife & Preise</h3>
              <span className="text-xs text-gray-500">Standard-Marge: {marginSettings.default_margin_ct_kwh} ct/kWh</span>
            </div>
            {supplierPrices.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FiDollarSign className="mx-auto mb-2" size={32} />
                <p>Noch keine Tarife hinterlegt</p>
                <Button variant="primary" icon={FiPlus} className="mt-3" size="sm" onClick={() => { setFormError(''); setShowPriceModal(true); }}>
                  Ersten Tarif anlegen
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {supplierPrices.map(price => {
                  const status = getPriceStatus(price);
                  const customerPrice = (price.price_ct_kwh ?? 0) + marginSettings.default_margin_ct_kwh;
                  return (
                    <div key={price.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900">{price.label}</p>
                            <span className={`px-1.5 py-0.5 text-xs rounded ${STATUS_STYLES[status]}`}>
                              {status === 'aktiv' ? 'Aktiv' : status === 'geplant' ? 'Geplant' : status === 'abgelaufen' ? 'Abgelaufen' : 'Inaktiv'}
                            </span>
                            {price.company_id && (
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Kundenspez.</span>
                            )}
                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              {price.price_type === 'fixed' ? 'Fest' : price.price_type === 'indexed' ? 'Index' : 'Dynamisch'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span>EK: <strong className="text-gray-700">{formatPriceCt(price.price_ct_kwh)} ct</strong></span>
                            <span>+{marginSettings.default_margin_ct_kwh} ct Marge</span>
                            <span>= <strong className="text-primary-700">{formatPriceCt(customerPrice)} ct</strong> VK</span>
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                            {price.contract_duration_months && <span>{price.contract_duration_months} Monate</span>}
                            {price.min_consumption_kwh && <span>ab {(price.min_consumption_kwh / 1000).toFixed(0)} MWh</span>}
                            <span>Gueltig: {formatDate(price.valid_from)} {price.valid_until ? `– ${formatDate(price.valid_until)}` : '(unbefristet)'}</span>
                            <span>Quelle: {price.source || '–'}</span>
                          </div>
                          {price.conditions && <p className="text-xs text-gray-400 mt-1">{price.conditions}</p>}
                        </div>
                        <button
                          onClick={() => handleDeletePrice(price)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                          title="Tarif loeschen"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Neuer Tarif Modal */}
      <Modal isOpen={showPriceModal} onClose={() => setShowPriceModal(false)} title="Neuer Tarif" size="lg">
        <PriceForm
          supplierId={id}
          onSave={handleAddPrice}
          onCancel={() => setShowPriceModal(false)}
          saving={saving}
          error={formError}
        />
      </Modal>

      {/* Versorger bearbeiten Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Versorger bearbeiten">
        {editForm && (
          <form onSubmit={handleSaveSupplier} className="space-y-4">
            {formError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className={inputClass} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ansprechpartner</label>
                <input value={editForm.contact_name} onChange={e => setEditForm(f => ({ ...f, contact_name: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                <input value={editForm.contact_email} onChange={e => setEditForm(f => ({ ...f, contact_email: e.target.value }))} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input value={editForm.contact_phone} onChange={e => setEditForm(f => ({ ...f, contact_phone: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
              <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} className={inputClass + ' h-16 resize-none'} />
            </div>
            <div className="flex justify-end space-x-3 pt-2 border-t">
              <Button type="button" variant="secondary" onClick={() => setShowEditModal(false)}>Abbrechen</Button>
              <Button type="submit" icon={FiSave} loading={saving} disabled={saving}>Speichern</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default SupplierDetail;
